/**
 * Generic Emarsys engagement event module.
 *
 * The EcoFlow proxy obtains the Emarsys access token server-side and forwards
 * the supplied payload. Business-specific callers only need to provide an
 * event type and payload. Standard event UUID paths and timestamp fields are
 * configured here; callers may override them through options when needed.
 *
 * Example:
 *   theme.EmarsysEngagement.trackEvent('VIEW_PAGE', payload);
 *
 * Failures are swallowed after being logged so tracking never blocks the page.
 */
window.theme = window.theme || {};

theme.EmarsysEngagement = theme.EmarsysEngagement || (function () {
  var config = (window.theme && window.theme.emarsysEngagementConfig) || {};
  var eventSettings = config.events || {};

  function str(value) {
    if (value === undefined || value === null) return '';
    return String(value);
  }

  function isoSeconds(date) {
    return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
  }

  function uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function formatPrice(cents) {
    if (cents === undefined || cents === null || cents === '') return '';
    var amount = Number(cents);
    if (!isFinite(amount)) return '';
    return amount / 100;
  }

  function clonePayload(payload) {
    return JSON.parse(JSON.stringify(payload || {}));
  }

  function setPath(target, path, value) {
    if (!Array.isArray(path) || !path.length) return;

    var current = target;
    for (var i = 0; i < path.length - 1; i++) {
      var key = path[i];
      if (!current[key] || typeof current[key] !== 'object') current[key] = {};
      current = current[key];
    }
    current[path[path.length - 1]] = value;
  }

  function buildEventUrl(eventType) {
    var baseUrl = str(config.engagementEventUrl);
    var encodedType = encodeURIComponent(eventType);

    if (/([?&])eventType=[^&]*/.test(baseUrl)) {
      return baseUrl.replace(/([?&])eventType=[^&]*/, '$1eventType=' + encodedType);
    }
    return baseUrl + (baseUrl.indexOf('?') === -1 ? '?' : '&') + 'eventType=' + encodedType;
  }

  function normalizePayload(payload, options) {
    var normalized = clonePayload(payload);
    var settings = options || {};

    normalized.customer = normalized.customer || {};
    if (normalized.customer.email === undefined || normalized.customer.email === null) {
      normalized.customer.email = str(config.email);
    }

    if (settings.eventIdPath) {
      setPath(normalized, settings.eventIdPath, uuid());
    }
    if (settings.timestampField && !normalized[settings.timestampField]) {
      normalized[settings.timestampField] = isoSeconds(new Date());
    }

    return normalized;
  }

  function trackEvent(eventType, payload, options) {
    // Include payload construction and fetch setup in the caught promise chain.
    return Promise.resolve().then(function () {
      var settings = Object.assign({}, eventSettings[eventType] || {}, options || {});
      if (!eventType || !config.engagementEventUrl || !str(config.xAppid).trim() ||
          !Array.isArray(settings.eventIdPath) || !settings.eventIdPath.length ||
          !settings.timestampField) {
        throw new Error('engagement event configuration is incomplete');
      }
      var normalizedPayload = normalizePayload(payload, settings);
      return fetch(buildEventUrl(eventType), {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          'x-appid': str(config.xAppid)
        },
        body: JSON.stringify(normalizedPayload)
      });
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('engagement event proxy request failed: ' + response.status);
      }
      return response.json();
    }).then(function (result) {
      if (!result || (result.code !== 0 && result.code !== '0')) {
        throw new Error('engagement event proxy rejected ' + eventType + ': ' +
          (result && result.code) + (result && result.message ? ' ' + result.message : ''));
      }
      return result;
    }).catch(function (error) {
      console.warn('[EmarsysEngagement] ' + eventType + ' event not reported:', error);
      return null;
    });
  }

  function findVariant(productData, variantId) {
    var variants = (productData && productData.variants) || [];
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(variantId)) return variants[i];
    }
    return null;
  }

  function trackAddToCart(productData, variantId) {
    var email = str(config.email).trim();
    if (!productData || !email) return Promise.resolve(null);

    var variant = findVariant(productData, variantId);
    return trackEvent(
      'ADD_TO_CART',
      {
        customer: {
          email: email,
          sku: variant ? str(variant.sku) : '',
          variant_id: variant && variant.id != null ? variant.id : '',
          final_price: variant ? formatPrice(variant.price) : '',
          cart_item: '',
          category: str(config.category)
        }
      }
    );
  }

  function trackViewPage(productData, variantId) {
    var email = str(config.email).trim();
    if (!productData || !email) return Promise.resolve(null);

    var variant = findVariant(productData, variantId);

    return trackEvent(
      'VIEW_PAGE',
      {
        customer: {
          email: email,
          title: str(productData.title),
          sku: variant ? str(variant.sku) : '',
          variant_id: variant && variant.id != null ? variant.id : '',
          final_price: variant ? formatPrice(variant.price) : '',
          browse_item: str(productData.title),
          category: str(config.category)
        }
      }
    );
  }

  function trackCartMove(item) {
    var email = str(config.email).trim();
    if (!item || !email) return Promise.resolve(null);

    return trackEvent(
      'CART_MOVE',
      {
        customer: {
          email: email,
          title: str(item.title),
          sku: str(item.sku),
          variant_id: str(item.variantId),
          final_price: formatPrice(item.finalPrice),
          cart_move_item: str(item.title),
          category: str(item.category)
        }
      }
    );
  }

  return {
    createEventId: uuid,
    formatTimestamp: isoSeconds,
    trackEvent: trackEvent,
    trackAddToCart: trackAddToCart,
    trackViewPage: trackViewPage,
    trackCartMove: trackCartMove
  };
})();

// Backwards-compatible alias for the existing PDP integration.
theme.EmarsysTracker = theme.EmarsysEngagement;
